import {
  useGetRunFailureQuery,
  useGetRunStallQuery,
  useListStagesQuery
} from '@/services/api';
import { ErrorState } from './ErrorState';
import { SkeletonGridRow } from './Skeleton';
import { formatTime } from '@/utils/format';
import { isTransportOrServerError } from '@/utils/apiError';
import {
  failureTypeLabel,
  isOperatorDecision,
  type FailureRecordDto,
  type StallDetailDto,
  type StageSummaryDto
} from '@/types/api';

type ErrorPanel = 'failure' | 'stall';

export function ErrorsTab({
  runId,
  panel,
  onPanelChange,
  refreshIntervalMs = 0
}: {
  runId: string;
  panel?: ErrorPanel;
  onPanelChange: (p: ErrorPanel) => void;
  refreshIntervalMs?: number;
}): JSX.Element {
  const active = panel ?? 'failure';
  return (
    <section data-testid="errors-tab">
      <div
        className="flex gap-1.5 mb-4"
        role="tablist"
        aria-label="Error panel"
      >
        <PanelTab label="Failure detail" active={active === 'failure'} onClick={() => onPanelChange('failure')} testId="errors-tab-failure" />
        <PanelTab label="Stall detail" active={active === 'stall'} onClick={() => onPanelChange('stall')} testId="errors-tab-stall" />
      </div>

      {active === 'failure' ? <FailurePanel runId={runId} refreshIntervalMs={refreshIntervalMs} /> : null}
      {active === 'stall' ? <StallPanel runId={runId} refreshIntervalMs={refreshIntervalMs} /> : null}
    </section>
  );
}

function PanelTab({
  label,
  active,
  onClick,
  testId
}: {
  label: string;
  active: boolean;
  onClick: () => void;
  testId: string;
}): JSX.Element {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      data-testid={testId}
      className={`px-3 py-1 text-[11.5px] rounded-md border ${
        active
          ? 'bg-accent border-accent text-white'
          : 'bg-panel-2 border-border text-text-dim hover:text-text'
      }`}
    >
      {label}
    </button>
  );
}


function FailurePanel({
  runId,
  refreshIntervalMs
}: {
  runId: string;
  refreshIntervalMs: number;
}): JSX.Element {
  const resp = useGetRunFailureQuery(runId, {
    pollingInterval: refreshIntervalMs,
    skipPollingIfUnfocused: true
  });

  if (resp.isLoading) {
    return (
      <div data-testid="failure-panel-loading">
        <SkeletonGridRow cols={3} />
      </div>
    );
  }
  if (resp.isError && isTransportOrServerError(resp.error)) {
    return (
      <div data-testid="failure-panel-error">
        <ErrorState
          title="Couldn't load failure details"
          body="The failure record could not be retrieved (server or network error). This does not mean the run is healthy — retry to check again."
          onRetry={() => void resp.refetch()}
        />
      </div>
    );
  }
  if (resp.isError || !resp.data) {
    return <FailureStageFallback runId={runId} refreshIntervalMs={refreshIntervalMs} />;
  }
  return <FailureDetail runId={runId} failure={resp.data} />;
}

function FailureStageFallback({
  runId,
  refreshIntervalMs
}: {
  runId: string;
  refreshIntervalMs: number;
}): JSX.Element {
  const resp = useListStagesQuery(runId, {
    pollingInterval: refreshIntervalMs,
    skipPollingIfUnfocused: true
  });
  if (resp.isLoading) {
    return (
      <div data-testid="failure-fallback-loading">
        <SkeletonGridRow cols={3} />
      </div>
    );
  }
  const failedStage: StageSummaryDto | undefined = resp.data?.stages.find(
    (s) => s.state === 'failed'
  );
  if (!failedStage) {
    return (
      <div data-testid="failure-panel-empty">
        <ErrorState
          title="No failure record for this run"
          body="The run did not fail, or the failure record is unavailable."
        />
      </div>
    );
  }
  return (
    <article
      className="bg-panel border border-border rounded-lg overflow-hidden"
      data-testid="failure-detail-fallback"
    >
      <header className="px-4 py-3 border-b border-border flex items-center gap-3">
        <div className="font-semibold text-[14px]">Failure detail · {runId}</div>
        <span className="text-[11px] px-2 py-0.5 rounded-[10px] font-medium bg-err/15 text-err">
          failed
        </span>
      </header>
      <div className="p-4">
        <div className="grid grid-cols-2 gap-4 mb-4 p-3 bg-panel-2 rounded-md">
          <FieldCell label="failed_at_stage" value={failedStage.stage_id} mono />
          <FieldCell label="failed_at_agent" value={failedStage.agent_id} mono />
        </div>
        <div className="grid grid-cols-1 gap-4 mb-4 p-3 bg-panel-2 rounded-md">
          <FieldCell
            label="failure_cause"
            value={failedStage.failure_reason ?? 'Stage failed (no cause recorded).'}
            danger
            scroll
          />
        </div>
        <div className="text-[11px] text-text-muted">
          Showing the failed stage&apos;s cause — a structured failure record was not
          recorded for this run.
        </div>
      </div>
    </article>
  );
}

function FailureDetail({
  runId,
  failure
}: {
  runId: string;
  failure: FailureRecordDto;
}): JSX.Element {
  return (
    <article
      className="bg-panel border border-border rounded-lg overflow-hidden"
      data-testid="failure-detail"
    >
      <header className="px-4 py-3 border-b border-border flex items-center gap-3">
        <div className="font-semibold text-[14px]">Failure detail · {runId}</div>
        <span className="text-[11px] px-2 py-0.5 rounded-[10px] font-medium bg-err/15 text-err">
          failed
        </span>
      </header>
      <div className="p-4">
        <div className="grid grid-cols-3 gap-4 mb-4 p-3 bg-panel-2 rounded-md">
          <FieldCell label="failed_at_stage" value={failure.failed_at_stage} mono />
          <FieldCell label="failed_at_agent_role" value={failure.failed_at_agent_role} mono />
          <FieldCell
            label="failure_type"
            value={failureTypeLabel(failure.failure_type)}
            danger={!isOperatorDecision(failure.failure_type)}
          />
        </div>
        <div className="grid grid-cols-1 gap-4 mb-4 p-3 bg-panel-2 rounded-md">
          <FieldCell label="failure_cause" value={failure.failure_cause} scroll />
        </div>

        <div className="text-[11.5px] text-text-muted uppercase tracking-wider mb-2">
          State snapshot at failure
        </div>
        <pre
          className="bg-panel-3 border border-border rounded-md p-3 font-mono text-[11.5px] text-text whitespace-pre-wrap leading-relaxed"
          data-testid="failure-state-snapshot"
        >
          {JSON.stringify(failure.state_snapshot, null, 2)}
        </pre>
      </div>
    </article>
  );
}

function FieldCell({
  label,
  value,
  mono = false,
  danger = false,
  warn = false,
  scroll = false
}: {
  label: string;
  value: string;
  mono?: boolean;
  danger?: boolean;
  warn?: boolean;
  scroll?: boolean;
}): JSX.Element {
  const cls = danger ? 'text-err font-medium' : warn ? 'text-warn font-medium' : 'text-text';
  const bounds = scroll ? 'max-h-64 overflow-y-auto overscroll-contain break-words' : '';
  return (
    <div className="min-w-0">
      <div className="text-[10.5px] text-text-muted uppercase tracking-wider mb-1">{label}</div>
      <div className={`text-[12.5px] ${cls} ${mono ? 'font-mono text-xs' : ''} ${bounds}`}>
        {value}
      </div>
    </div>
  );
}


function StallPanel({
  runId,
  refreshIntervalMs
}: {
  runId: string;
  refreshIntervalMs: number;
}): JSX.Element {
  const resp = useGetRunStallQuery(runId, {
    pollingInterval: refreshIntervalMs,
    skipPollingIfUnfocused: true
  });
  if (resp.isLoading) {
    return (
      <div data-testid="stall-panel-loading">
        <SkeletonGridRow cols={3} />
      </div>
    );
  }
  if (resp.isError && isTransportOrServerError(resp.error)) {
    return (
      <div data-testid="stall-panel-error">
        <ErrorState
          title="Couldn't load stall details"
          body="The stall record could not be retrieved (server or network error). This does not confirm the run is healthy — retry to check again."
          onRetry={() => void resp.refetch()}
        />
      </div>
    );
  }
  if (resp.isError || !resp.data) {
    return (
      <div className="text-center text-text-muted text-[12.5px] py-6" data-testid="stall-empty">
        Run is not stalled.
      </div>
    );
  }
  return <StallDetail stall={resp.data} />;
}

function StallDetail({ stall }: { stall: StallDetailDto }): JSX.Element {
  return (
    <article
      className="bg-panel border border-border rounded-lg overflow-hidden"
      data-testid="stall-detail"
    >
      <header className="px-4 py-3 border-b border-border flex items-center gap-3">
        <div className="font-semibold text-[14px]">Stall detail · {stall.pipeline_run_id}</div>
        <span className="text-[11px] px-2 py-0.5 rounded-[10px] font-medium bg-warn/15 text-warn">
          stalled
        </span>
      </header>
      <div className="p-4">
        <div className="grid grid-cols-3 gap-4 mb-4 p-3 bg-panel-2 rounded-md">
          <FieldCell label="stall_cause" value={stall.stall_cause} warn />
          <FieldCell label="detected_at" value={formatTime(stall.detected_at)} mono />
          <FieldCell
            label="no_progress_for"
            value={`${Math.round(stall.no_progress_for_ms / 60_000)}m ${Math.round((stall.no_progress_for_ms % 60_000) / 1000)}s`}
          />
        </div>
        <div className="text-[11.5px] text-text-muted uppercase tracking-wider mb-2">
          Stage / agent state at detection
        </div>
        <pre
          className="bg-panel-3 border border-border rounded-md p-3 font-mono text-[11.5px] text-text whitespace-pre-wrap leading-relaxed"
          data-testid="stall-state-snapshot"
        >
          {JSON.stringify(
            {
              stalled_stage: stall.stalled_stage,
              agent_id: stall.agent_id,
              last_event: stall.last_event,
              last_event_at: stall.last_event_at,
              pid_alive: stall.pid_alive,
              socket_responsive: stall.socket_responsive
            },
            null,
            2
          )}
        </pre>
      </div>
    </article>
  );
}
